import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import AcceptedPayments from "@/components/AcceptedPayments";

/**
 * Slim footer with legal + safety links. Rendered once at the bottom of the
 * shell so every public page gets it.
 */
export default function Footer() {
  return (
    <footer
      className="mt-16 border-t border-stone-200 bg-stone-50/60"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid sm:grid-cols-3 gap-6 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <img src="/logo-mark.png" alt="COMPANIN" className="h-7 w-7 rounded-lg bg-white shadow-sm" />
            <span className="font-display text-lg font-semibold text-stone-900">COMPANIN</span>
          </div>
          <p className="text-stone-500 text-xs leading-relaxed">
            Nobody should feel alone. Book a verified companion for genuine
            company — coffee, a walk, or a long talk.
          </p>
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-2">
            Trust &amp; Safety
          </h4>
          <ul className="space-y-1.5">
            <li>
              <Link
                to="/trust"
                className="inline-flex items-center gap-1.5 text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
                data-testid="footer-trust-link"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Trust &amp; safety stats
              </Link>
            </li>
            <li>
              <Link
                to="/safety"
                className="inline-flex items-center gap-1.5 text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
                data-testid="footer-safety-link"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Safety guidelines
              </Link>
            </li>
            <li>
              <a
                href="mailto:care@companin.com"
                className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
              >
                care@companin.com
              </a>
            </li>
            <li className="text-stone-400 text-xs">
              In an emergency, call your local emergency number first.
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-2">
            Legal
          </h4>
          <ul className="space-y-1.5">
            <li>
              <Link
                to="/privacy"
                className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
                data-testid="footer-terms-link"
              >
                Terms &amp; Conditions
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("companin:open-cookie-settings"))}
                className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4 text-left"
                data-testid="footer-cookie-settings-link"
              >
                Cookie settings
              </button>
            </li>
            <li>
              <a
                href="mailto:care@companin.com"
                className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4"
              >
                care@companin.com
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-stone-200 px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-stone-400">
          © {new Date().getFullYear()} COMPANIN Booking Platform. All rights reserved.
        </p>
        <AcceptedPayments variant="row" className="w-full sm:max-w-md" />
      </div>
    </footer>
  );
}
